import dayjs from "dayjs";

/**
 * 57日後日付取得Hooks
 *
 * 57日後の日付を取得します。
 * @param dateString : 基準日付
 * @return yyyy/MM/dd
 **/
const getDate57DaysLater = (yyyymmdd: string) => {
  const currentDate = dayjs(yyyymmdd);
  const resultDate = currentDate.add(57, "day");
  return resultDate.format("YYYY/MM/DD");
};

/**
 * 満1年日付取得Hooks
 *
 * 満1年（対象日付+1年-1日）の日付を取得します。
 * @param dateString : 基準日付
 * @return yyyy/MM/dd
 **/
const getOneYearDate = (yyyymmdd: string) => {
  const currentDate = dayjs(yyyymmdd);
  const resultDate = currentDate.add(1, "year").subtract(1, "day");
  return resultDate.format("YYYY/MM/DD");
};

/**
 *「出勤日数」の対象年月作成（年月取得）Hooks
 *
 * 年月日の日付の場合、年月を返却します。
 * @param dateString : 対象日付
 * @return 年月(YYYYMM)
 **/
const getYearMonth = (dateString: string | undefined): number => {
  if (dateString === undefined) {
    return -1;
  }

  if (!dayjs(dateString).isValid()) {
    return -1;
  }

  return Number(dayjs(dateString).format("YYYYMM"));
};

/**
 * 「出勤日数」の対象年月作成（育児休業開始日がその月の12日以降）Hooks
 *
 * 12日以降の日付の場合、年月を返却します。
 * @param dateString : 対象日付
 * @return 年月(YYYYMM)
 **/
const getYearMonthAfterTwelfth = (dateString: string | undefined): number => {
  if (dateString === undefined) {
    return -1;
  }

  if (!dayjs(dateString).isValid()) {
    return -1;
  }

  const currentDate = dayjs(dateString);
  const dayOfMonth = currentDate.date();
  if (dayOfMonth >= 12) {
    return Number(currentDate.format("YYYYMM"));
  } else {
    return -1;
  }
};

/**
 * 「出勤日数」の対象年月作成（育児休業終了（予定）日が月末日-10日よりも前）Hooks
 *
 * 育児休業終了（予定）日が月末日-10日よりも前の場合、年月を返却します。
 * @param dateString : 対象日付
 * @return 年月(YYYYMM)
 **/
const getYearMonthElevenDaysBeforeMonthEnd = (
  dateString: string | undefined
): number => {
  if (dateString === undefined) {
    return -1;
  }

  if (!dayjs(dateString).isValid()) {
    return -1;
  }

  const currentDate = dayjs(dateString);
  const endOfMonth = currentDate.endOf("month");
  const remainingDays = endOfMonth.diff(currentDate, "day");
  if (remainingDays > 10) {
    return Number(currentDate.format("YYYYMM"));
  } else {
    return -1;
  }
};

/**
 * 「出勤日数」の対象年月作成（同年同月の場合は、その月の育休対象外期間が11日以上）Hooks
 *
 * 「育児休業開始日」と「育児休業終了（予定）日」が同年同月の場合は、その月の育休対象外期間が11日以上の場合は対象となる。
 * @param startDate : 育児休業開始日
 * @param endDate : 育児休業終了（予定）日
 * @return 年月(YYYYMM)
 **/
const getYearMonthHasMoreThanElevenDays = (
  startDate: string | undefined,
  endDate: string | undefined
): number => {
  if (startDate === undefined || endDate === undefined) {
    return -1;
  }

  if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
    return -1;
  }

  const startYearMonth = dayjs(startDate).format("YYYY/MM");
  const endYearMonth = dayjs(endDate).format("YYYY/MM");
  if (startYearMonth !== endYearMonth) {
    return -1;
  }

  const days =
    dayjs(startDate).daysInMonth() -
    dayjs(endDate).diff(dayjs(startDate), "day") -
    1;
  if (days >= 11) {
    return Number(dayjs(startDate).format("YYYYMM"));
  } else {
    return -1;
  }
};

/**
 * 同じ年月かどうかを判定するHooks
 *
 * 同じ年月の場合、trueを返却します。同じ年月ではない場合、falseを返却します。
 * @param yearMonth1 : 比較対象年月
 * @param yearMonth2 : 比較対象年月
 * @return 判定結果
 **/
const isSameYearMonth = (
  yearMonth1: number | undefined,
  yearMonth2: number
) => {
  if (yearMonth1 === undefined) return false;
  const year1 = Math.floor(yearMonth1 / 100);
  const month1 = yearMonth1 % 100;
  const year2 = Math.floor(yearMonth2 / 100);
  const month2 = yearMonth2 % 100;
  const format = "YYYY/MM";
  return dayjs(`${year1}/${month1}`, format).isSame(
    dayjs(`${year2}/${month2}`, format)
  );
};

export {
  getDate57DaysLater,
  getOneYearDate,
  getYearMonth,
  getYearMonthAfterTwelfth,
  getYearMonthElevenDaysBeforeMonthEnd,
  getYearMonthHasMoreThanElevenDays,
  isSameYearMonth,
};
